import { PropsWithChildren, useEffect } from 'react';
import {
    FieldError,
    UseFormClearErrors,
    UseFormRegister,
    UseFormSetError,
    UseFormSetValue,
} from 'react-hook-form';
import {
    AspectRatio,
    BasicResponsiveImage,
    Box,
    Checkbox,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    Row,
    StandardTile,
    StandardTileImage,
    StandardTileOverlay,
    Typography,
    tokensRaw,
} from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { useBriefContext } from '../../../BriefContext/BriefContext';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { FormInput } from '../../BriefForm';
import { Field_IntelligentStylesField_Fragment } from '../../brief.generated';
import { IntelligentStyleDesignsQuery } from './IntelligentStyleDesigns.generated';
import { ProgressIndicator } from './ProgressIndicator';
import { DesignOptions, useDesignOptions } from './useDesignOptions';
import { useUpdateIntelligentStylesField } from './useUpdateIntelligentStylesField';

export type IntelligentStylesFieldProps = Field_IntelligentStylesField_Fragment & {
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
    clearErrors: UseFormClearErrors<FormInput>;
    setError: UseFormSetError<FormInput>;
    error: FieldError | undefined;
};

export type InspirationDesign = Omit<
    IntelligentStyleDesignsQuery['expertDesigns']['designs'][0],
    '__typename'
>;

const MAX_SELECTED = DesignOptions.MAX_SELECTED;

export function Input({
    id,
    intelligentStylesValue,
    setValue,
    clearErrors,
    setError,
    register,
    required,
    errorMessage,
    error,
}: IntelligentStylesFieldProps) {
    const { briefId } = useBriefFormContext();
    const { product } = useBriefContext();
    const errorMsg = errorMessage || __('This field is required');

    const { update: updateMutation } = useUpdateIntelligentStylesField(
        id,
        briefId,
        intelligentStylesValue
    );

    const { designOptions, setSelected, selected, hasChanged, loading } = useDesignOptions(
        intelligentStylesValue.designs,
        product?.fulfilmentMpvId || undefined
    );

    const totalSelected = designOptions.totalSelected();

    useEffect(() => {
        register(id, {
            required: required ? errorMsg : false,
            validate: (value) => {
                const selectedDesigns = value?.designs || [];
                if (required && selectedDesigns.length === 0) {
                    return errorMsg;
                }
                return true;
            },
        });
        setValue(id, intelligentStylesValue);
        clearErrors(id);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const selectedDesigns = designOptions.selectedDesigns();
        const newValue = {
            designs: selectedDesigns,
        };

        setValue(id, newValue);

        if (required) {
            if (selectedDesigns.length === 0 && hasChanged) {
                setError(id, { type: 'required', message: errorMsg });
                return;
            }
        }

        if (hasChanged) {
            clearErrors(id);
            updateMutation(newValue);
        }
    }, [
        updateMutation,
        designOptions,
        id,
        clearErrors,
        setValue,
        required,
        setError,
        errorMsg,
        hasChanged,
    ]);

    const skeletonComponents = Array.from({ length: 30 }).map((_, idx) => (
        <IntelligentStylesColumn key={`intelligent-styles-skeleton-${idx}`}>
            <StandardTile as="span" mb={4} style={{ margin: 0 }}>
                <StandardTileImage as="span" backgroundColor="transparent">
                    <AspectRatio ratio={1} loadingShimmer />
                </StandardTileImage>
            </StandardTile>
        </IntelligentStylesColumn>
    ));

    const designComponents = designOptions.all().map(({ isSelected, ...design }) => {
        const shouldDisable = !isSelected && totalSelected >= MAX_SELECTED;
        const tileStyle = { cursor: shouldDisable ? 'not-allowed' : 'pointer', margin: 0 };
        const tileImageStyle = {
            outline: isSelected
                ? `${tokensRaw.SwanBaseBorderWidth200} solid ${tokensRaw.SwanSemColorBorderSelected}`
                : 'none',
            borderRadius: tokensRaw.SwanSemBorderRadiusSubtle,
        };

        return (
            <IntelligentStylesColumn>
                <StandardTile as="span" mb={4} style={tileStyle}>
                    <StandardTileOverlay style={{ justifyContent: 'right' }}>
                        <Checkbox
                            name={id}
                            checked={isSelected}
                            skin="favorite"
                            disabled={shouldDisable}
                            aria-required={required ? true : undefined}
                            instant
                            value={design.id}
                            aria-describedby={id}
                            onChange={() => {
                                if (shouldDisable) {
                                    return;
                                }
                                setSelected({
                                    ...selected,
                                    [design.id]: !isSelected,
                                });
                            }}
                        />
                    </StandardTileOverlay>
                    <StandardTileImage
                        as="span"
                        backgroundColor={'transparent'}
                        style={{
                            ...tileImageStyle,
                            opacity: shouldDisable ? 0.4 : 1,
                        }}
                    >
                        <BasicResponsiveImage
                            aspectRatio={1}
                            src={design.thumbnailUri}
                            alt={design.id}
                            draggable={false}
                            style={{
                                borderRadius: tokensRaw.SwanSemBorderRadiusSubtle,
                            }}
                        />
                    </StandardTileImage>
                </StandardTile>
            </IntelligentStylesColumn>
        );
    });

    return (
        <FlexBox
            flexDirection="column"
            gap="5"
            aria-labelledby={id}
            style={{ marginTop: error ? '19px' : undefined }} // margin to align with error message  with progress indicator
        >
            {!error && <ProgressIndicator value={(totalSelected / MAX_SELECTED) * 100} />}
            <GridContainer>
                <Row style={{ margin: `0 -${tokensRaw.SwanSemSpace3}` }}>
                    {loading ? skeletonComponents : designComponents}
                </Row>
            </GridContainer>
        </FlexBox>
    );
}

function IntelligentStylesColumn(props: PropsWithChildren) {
    return (
        <Column span={4} spanXs={6} paddingX={3} marginBottom={2}>
            <label>{props.children}</label>
        </Column>
    );
}

type IntelligentStylesFieldViewProps = {
    intelligentStylesValue: {
        computed: string | null;
        designs: {
            id: string;
            thumbnailUri: string;
        }[];
    };
    options: {
        value: string;
        label: string;
        subtext: string | null;
    }[];
};

export function IntelligentStylesReview({
    intelligentStylesValue,
    options,
}: IntelligentStylesFieldViewProps) {
    const computedValueData = options.find((opt) => opt.value === intelligentStylesValue.computed);

    useEffect(() => {
        if (!intelligentStylesValue.computed) {
            bugtracker.notify(new Error('IntelligentStylesReview called with null computed value'));
        }
    }, [intelligentStylesValue.computed]);

    return (
        <Box>
            {computedValueData && computedValueData.subtext && (
                <Box>
                    <Typography fontSkin={'body-small'}>{__('Your Style')}</Typography>
                    <Typography fontSkin={'body-standard'}>
                        <strong>{computedValueData.label}:&nbsp;</strong>
                        {computedValueData.subtext}
                    </Typography>
                </Box>
            )}
            <FlexBox flexWrap={'wrap'} gap={3} mt={5}>
                {intelligentStylesValue.designs.map((design) => (
                    <FluidImage
                        key={design.id}
                        style={{
                            height: '64px',
                            width: '64px',
                            borderRadius: tokensRaw.SwanSemBorderRadiusSubtle,
                        }}
                        src={design.thumbnailUri}
                        alt={design.id}
                    />
                ))}
            </FlexBox>
        </Box>
    );
}
